import { call, delay, fork, put, select } from "redux-saga/effects";
import { getAppStoreName } from "constants/AppConstants";
// import localStorage from "utils/localStorage";
import { updateAppStore } from "actions/pageActions";
import AppsmithConsole from "utils/AppsmithConsole";
import { getAppStoreData } from "ee/selectors/entitiesSelector";
import { getCurrentGitBranch } from "selectors/gitSyncSelectors";
import { getCurrentApplicationId } from "selectors/editorSelectors";
import type { AppStoreState } from "reducers/entityReducers/appReducer";
import { Severity, LOG_CATEGORY } from "entities/AppsmithConsole";
import moment from "moment";
import type {
  TClearStoreDescription,
  TRemoveValueDescription,
  TStoreValueDescription,
} from "workers/Evaluation/fns/storeFns";
import { isBoolean } from "lodash";
import { message } from "antd";
import { evalWorker } from "sagas/EvaluationsSaga";
import showAlert from "workers/Evaluation/fns/showAlert";

type StoreOperation =
  | TStoreValueDescription
  | TClearStoreDescription
  | TRemoveValueDescription;

const lastExecutionTimes = new Map();
const debounceState = new Map<
  string,
  {
    message: any;
    lastUpdateTime: number;
    timerRunning: boolean;
  }
>();

export function* handleStoreOperations(triggers: StoreOperation[]) {
  const applicationId: string = yield select(getCurrentApplicationId);
  const branch: string | undefined = yield select(getCurrentGitBranch);
  const appStoreName = getAppStoreName(applicationId, branch);
  const existingLocalStore = localStorage.getItem(appStoreName) || "{}";
  let parsedLocalStore = JSON.parse(existingLocalStore);
  let currentStore: AppStoreState = yield select(getAppStoreData);
  const logs: string[] = [];
  const trigger = triggers[0];
  const {
    triggerSetting,
    switchValue,
    debounceAndThrottleTime,
    executeCondition,
    key,
  } = trigger.payload;
  const time = debounceAndThrottleTime ? parseInt(debounceAndThrottleTime.toString(), 10) : 1000;
  const condition =
    executeCondition === "true" ||
    (isBoolean(executeCondition) && executeCondition);

  const fn = () => {
    const storeString = JSON.stringify(parsedLocalStore);
    localStorage.setItem(appStoreName, storeString);
    AppsmithConsole.addLogs(
      logs.map((text) => ({
        text,
        severity: Severity.INFO,
        category: LOG_CATEGORY.PLATFORM_GENERATED,
        timestamp: moment().format("HH:mm:ss"),
        isExpanded: false,
      })),
    );
  };
  for (const t of triggers) {
    const { type } = t;
    if (type === "STORE_VALUE") {
      const { key, persist, value } = t.payload;
      if (persist) {
        parsedLocalStore[key] = value;
      }
      currentStore[key] = value;
      logs.push(`storeValue('${key}', '${value}', ${persist})`);
    } else if (type === "REMOVE_VALUE") {
      const { key } = t.payload;
      delete parsedLocalStore[key];
      delete currentStore[key];
      logs.push(`removeValue('${key}')`);
    } else if (type === "CLEAR_STORE") {
      parsedLocalStore = {};
      currentStore = {};
      logs.push(`clearStore()`);
    }
  }
  const triggerId = key;
  if (switchValue && !condition) {
    message.error("执行条件被限制，需联系管理员配置");
    return;
  }
  if (switchValue && condition) {
    if (triggerSetting === "throttle") {
      const now = Date.now();
      const lastExecution = lastExecutionTimes.get(triggerId) || 0;
      // 如果距离上次执行的时间小于节流时间，则跳过
      if (now - lastExecution < time) {
        return;
      }
      // 更新最后执行时间
      lastExecutionTimes.set(triggerId, now);
      yield put(updateAppStore(currentStore));
      fn();
      return;
    } else if (triggerSetting === "debounce") {
      const now = Date.now();
      const prev = debounceState.get(triggerId);
      debounceState.set(triggerId, {
        message: key,
        lastUpdateTime: now,
        timerRunning: prev?.timerRunning ?? false,
      });
      // 如果 timer 没在运行，启动一个新的监听任务
      if (!prev?.timerRunning) {
        debounceState.set(triggerId, {
          ...debounceState.get(triggerId)!,
          timerRunning: true,
        });
        yield fork(function* () {
          while (true) {
            yield delay(time);
            const current = debounceState.get(triggerId);
            if (!current) break;
            const sinceLastUpdate = Date.now() - current.lastUpdateTime;
            if (sinceLastUpdate >= time) {
              yield put(updateAppStore(currentStore));
              fn();
              debounceState.delete(triggerId);
              break;
            }
          }
        });
      }
    } else {
      yield put(updateAppStore(currentStore));
      fn();
    }
  } else {
    yield put(updateAppStore(currentStore));
    fn();
  }
}
